/**
 * Sticky-graph hook — keeps the last *non-empty* graph value on screen
 * even if the upstream source momentarily yields an empty / null one.
 *
 * Background: every workbench surface that renders a topology
 * (Build artifact panel, Agents tab topology aside, Room MiniFlow,
 * Execution history thumbnails) was independently flickering whenever
 * its source updated mid-stream — a silent ``GET /networks/{id}``
 * refetch returned no ``latestGraph``, a replayed ``ARTIFACT_EMIT``
 * envelope carried an empty payload, or a release fetch was retrying.
 * Each surface had its own ad-hoc guard (or lacked one), so the bug
 * was a moving target.
 *
 * This hook centralizes the rule:
 *   * If the incoming value is a real graph — render it.
 *   * If the incoming value is empty / null / loading — keep showing
 *     whatever was last good.
 *   * The caller decides what counts as "real" via ``hasContent``
 *     (defaults to ReactFlow ``{nodes, edges}`` shape).
 *   * A non-empty caller-driven reset key (e.g. ``networkId``) wipes
 *     the cache so switching networks doesn't bleed the previous
 *     graph through.
 *
 * Usage:
 *   const stickyGraph = useStickyGraph(maybeGraph, { resetKey: networkId });
 *   <GraphVisualization data={stickyGraph} />
 *
 * Or for raw ASL (Room MiniFlow):
 *   const stickyAsl = useStickyGraph(maybeAsl, {
 *     resetKey: networkId,
 *     hasContent: (a) => !!(a && (a.States || a.states)),
 *   });
 */
import { useEffect, useRef, useState } from 'react';

const defaultHasReactFlow = (g) =>
  !!g && typeof g === 'object'
  && ((g.nodes && g.nodes.length) || (g.edges && g.edges.length));

export function useStickyGraph(value, { resetKey = null, hasContent = defaultHasReactFlow } = {}) {
  const [held, setHeld] = useState(() => (hasContent(value) ? value : null));
  const lastResetKeyRef = useRef(resetKey);

  useEffect(() => {
    // Reset boundary — caller signals a new context (different network,
    // different release, etc.). Drop the held graph so we don't
    // visually leak the previous context's topology onto the new one.
    if (resetKey !== lastResetKeyRef.current) {
      lastResetKeyRef.current = resetKey;
      setHeld(hasContent(value) ? value : null);
      return;
    }
    if (hasContent(value)) {
      setHeld(value);
    }
    // ``value`` empty / null while ``resetKey`` is unchanged → keep ``held``.
  }, [value, resetKey, hasContent]);

  return held;
}

export default useStickyGraph;
